import React, { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddSelect from './AddSelect'
import RemovableList from './RemovableList'
import TextInput from './TextInput'
import {
    apiGetAdminData, apiCreateRole, apiDestroyRole, apiGrantCapability, apiRevokeCapability,
    apiAddScope, apiRemoveScope, apiSetScopeProjects, apiAddUserRole, apiRemoveUserRole,
    AdminData, AdminRole,
} from './Api'
import { CAPABILITIES, CAPABILITY_KIND, Capability, CapabilityKind } from './generated/capabilities'
import { CAPABILITY_LABEL, KIND_LABEL } from './capabilityLabels'
import ConfirmDialog from './ConfirmDialog'

const KINDS: CapabilityKind[] = ['scoped', 'company', 'admin']

// Small section heading used inside a role card.
function SubHeading({ children }: { children: React.ReactNode }) {
    return <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>{children}</Typography>
}

// A role editor: capabilities (grouped by kind), scope pairs and users as removable chips. Every
// change hits the backend then triggers a reload, so the view always mirrors the DB.
function RoleCard({ role, data, reload }: { role: AdminRole, data: AdminData, reload: () => void }) {
    const [openCc, setOpenCc] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    // Cost-centres kept visible in the editor even at zero projects — so clearing projects to
    // re-pick doesn't make the row vanish. Sticky for the session until an explicit "Ta bort";
    // a zero-project cost-centre has no persisted scope anyway, so it's purely a UI affordance.
    const [pinnedCcs, setPinnedCcs] = useState<Set<string>>(new Set())

    const held = new Set(role.capabilities)
    const inUse = role.user_ids.length > 0 || role.scope.length > 0
    // In-scope project ids grouped by cost-centre item — the scope editor is per cost-centre.
    const scopeByCc = new Map<string, Set<string>>()
    for (const pair of role.scope) {
        const set = scopeByCc.get(pair.cost_center_item_id) ?? new Set<string>()
        set.add(pair.project_id)
        scopeByCc.set(pair.cost_center_item_id, set)
    }
    const userLabel = (id: string) => {
        const u = data.users.find(u => u.id === id)
        return u ? (u.name || u.email) : id
    }

    // A cost-centre is an editable row if it has project pairs OR is pinned (being edited); the
    // rest are offered in the "add" dropdown.
    const isShown = (id: string) => (scopeByCc.get(id)?.size ?? 0) > 0 || pinnedCcs.has(id)
    const shownCcs = data.cost_center_items.filter(cc => isShown(cc.id))
    const availableCcs = data.cost_center_items.filter(cc => !isShown(cc.id))
    const pin = (id: string) => setPinnedCcs(prev => new Set(prev).add(id))

    const toggleCapability = (capability: Capability, on: boolean) =>
        (on ? apiGrantCapability(role.name, capability) : apiRevokeCapability(role.name, capability)).then(reload)
    const addUser = (id: string) => {
        if (!id) return
        apiAddUserRole(role.name, id).then(reload)
    }
    const addScope = (id: string) => {
        if (!id) return
        // Default a new ansvarsområde to all projects — the common case (a cost-centre + all/most);
        // pin + open it so the projects can be trimmed right away without the row vanishing.
        pin(id); setOpenCc(id)
        apiSetScopeProjects(role.name, id, data.projects.map(p => p.id)).then(reload)
    }
    const removeScope = (id: string) => {
        setPinnedCcs(prev => { const next = new Set(prev); next.delete(id); return next })
        if (openCc === id) setOpenCc(null)
        apiSetScopeProjects(role.name, id, []).then(reload)
    }

    return (
        <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="h6">{role.name}</Typography>
                {/* A role can only be removed once it's empty (the backend refuses otherwise), so
                    warn upfront: disable the button while it still has users or ansvarsområden.
                    A disabled button emits no hover events, so the tooltip wraps a <span>. */}
                <Tooltip title={inUse ? 'Ta bort rollens användare och ansvarsområden först' : ''}>
                    <span>
                        <Button color="error" size="small" disabled={inUse}
                            onClick={() => setConfirmDelete(true)}>Ta bort roll…</Button>
                    </span>
                </Tooltip>
            </Box>

            <SubHeading>Behörigheter</SubHeading>
            {KINDS.map(kind => (
                <Box key={kind} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{KIND_LABEL[kind]}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                        {CAPABILITIES.filter(c => CAPABILITY_KIND[c] === kind).map(capability => (
                            <FormControlLabel key={capability} sx={{ alignItems: 'flex-start', mr: 0, my: 0.25 }}
                                control={<Checkbox size="small" sx={{ py: 0, pr: 1 }} checked={held.has(capability)}
                                    onChange={e => toggleCapability(capability, e.target.checked)} />}
                                label={<Box sx={{ py: 0.25 }}>
                                    <Typography variant="body2">{CAPABILITY_LABEL[capability].label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{CAPABILITY_LABEL[capability].description}</Typography>
                                </Box>} />
                        ))}
                    </Box>
                </Box>
            ))}

            <Divider sx={{ my: 1.5 }} />
            <SubHeading>Ansvarsområden</SubHeading>
            {/* Only the cost-centres this role covers are shown — a role is typically one
                cost-centre + all/most projects, so the full list would be mostly noise. Add more
                from the dropdown below; each row expands to its projects ("Alla projekt" toggle). */}
            {shownCcs.length === 0
                ? <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Inga än.</Typography>
                : <Box sx={{ border: '1px solid #eee', borderRadius: 1, mb: 1 }}>
                    {shownCcs.map(cc => {
                        const inScope = scopeByCc.get(cc.id) ?? new Set<string>()
                        const total = data.projects.length
                        const allChecked = total > 0 && inScope.size === total
                        const summary = inScope.size === 0 ? 'inga projekt'
                            : allChecked ? 'alla projekt' : `${inScope.size} av ${total} projekt`
                        const expanded = openCc === cc.id
                        return (
                            <Box key={cc.id} sx={{ borderTop: '1px solid #eee', '&:first-of-type': { borderTop: 0 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75 }}>
                                    <Box onClick={() => { if (!expanded) pin(cc.id); setOpenCc(expanded ? null : cc.id) }}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, cursor: 'pointer', userSelect: 'none' }}>
                                        <Typography component="span" sx={{ width: '1rem', color: 'text.secondary' }}>{expanded ? '▾' : '▸'}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{cc.short_name} · {cc.name}</Typography>
                                        <Typography variant="caption" sx={{ color: inScope.size ? 'primary.main' : 'text.disabled' }}>{summary}</Typography>
                                    </Box>
                                    <Button size="small" color="error" sx={{ minWidth: 0, px: 1 }}
                                        onClick={() => removeScope(cc.id)}>Ta bort</Button>
                                </Box>
                                <Collapse in={expanded}>
                                    <Box sx={{ px: 2, pb: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={allChecked} indeterminate={inScope.size > 0 && !allChecked}
                                                onChange={e => apiSetScopeProjects(role.name, cc.id, e.target.checked ? data.projects.map(p => p.id) : []).then(reload)} />}
                                            label={<Typography variant="body2" sx={{ fontStyle: 'italic' }}>Alla projekt</Typography>} />
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                                            {data.projects.map(p => (
                                                <FormControlLabel key={p.id} sx={{ my: 0 }}
                                                    control={<Checkbox size="small" sx={{ py: 0.25 }} checked={inScope.has(p.id)}
                                                        onChange={e => (e.target.checked ? apiAddScope(role.name, cc.id, p.id) : apiRemoveScope(role.name, cc.id, p.id)).then(reload)} />}
                                                    label={<Typography variant="body2">{p.number} · {p.name}</Typography>} />
                                            ))}
                                        </Box>
                                    </Box>
                                </Collapse>
                            </Box>
                        )
                    })}
                  </Box>}
            {availableCcs.length > 0 &&
                <AddSelect label="Lägg till ansvarsområde" sx={{ minWidth: 280 }}
                    options={availableCcs} valueGetter={cc => cc.id} nameGetter={cc => `${cc.short_name} · ${cc.name}`}
                    onAdd={addScope}/>}

            <Divider sx={{ my: 1.5 }} />
            <SubHeading>Användare</SubHeading>
            <RemovableList items={role.user_ids} getKey={id => id} emptyText="Inga än."
                renderItem={id => <Typography variant="body2">{userLabel(id)}</Typography>}
                onRemove={id => apiRemoveUserRole(role.name, id).then(reload)} />
            <Box sx={{ mt: 0.5 }}>
                <AddSelect label="Lägg till användare" sx={{ minWidth: 280 }}
                    options={data.users.filter(u => !role.user_ids.includes(u.id))}
                    valueGetter={u => u.id} nameGetter={u => u.name ? `${u.name} (${u.email})` : u.email}
                    onAdd={addUser}/>
            </Box>

            <ConfirmDialog
                open={confirmDelete}
                title={`Ta bort rollen ”${role.name}”?`}
                message="Rollen tas bort permanent. Det går inte att ångra."
                confirmLabel="Ta bort roll"
                onConfirm={() => apiDestroyRole(role.name).then(() => { setConfirmDelete(false); reload() })}
                onCancel={() => setConfirmDelete(false)}
            />
        </Card>
    )
}

export default function Admin() {
    const [data, setData] = useState<AdminData | null>(null)
    const [newRoleName, setNewRoleName] = useState('')
    const reload = useCallback(() => { apiGetAdminData().then(setData).catch(() => {}) }, [])
    useEffect(() => { reload() }, [reload])

    if (data === null) return null

    const createRole = () => {
        const name = newRoleName.trim()
        if (!name) return
        apiCreateRole(name).then(() => { setNewRoleName(''); reload() })
    }

    return (
        <Box sx={{ p: 2, maxWidth: 960 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Roller &amp; behörigheter</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                En roll = behörigheter × ansvarsområden. Tilldela roller till användare; ändringar sparas direkt.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mb: 2 }}>
                <TextInput label="Ny roll" value={newRoleName} onChange={setNewRoleName}
                    onKeyDown={e => { if (e.key === 'Enter') createRole() }} />
                <Button variant="contained" disabled={!newRoleName.trim()} onClick={createRole}>Skapa roll</Button>
            </Box>
            {data.roles.map(role => <RoleCard key={role.id} role={role} data={data} reload={reload} />)}
        </Box>
    )
}
